import { useCallback, useEffect, useMemo, useState } from 'react';
import { showToastrMessage } from '@/utilities/Toastr.Utils';
import { validateZipCode } from '@/utilities/FormUtils';
import { useCalculateSalesTaxMutation } from '@/api/OrderQuery';
import type { IError } from '@/common/interfaces/error.interface';
import type {
  IOrderSummaryProps,
  IRegularOrderSizeForm,
  IReviewOrderItem,
} from '@/common/interfaces/order.interface';
import type { PromoCodeData } from '@/common/interfaces/promoCode.interface';
import { resolvePromoDiscount, shouldUseDiscountedSalesTaxForPromo } from '@/utilities/OrderUtils';

interface ISalesTaxSuccessRes {
  data: {
    salesTax: {
      salesTaxAmount: number;
      discountedSalesTaxAmount: number;
    };
    status: string;
    message?: string;
  };
}

interface IRegularOrderSummary
  extends Pick<IOrderSummaryProps, 'items' | 'sizeFormValues' | 'shippingFormValues'> {
  fetchSalesTax: boolean;
  promoCodeData: PromoCodeData | undefined;
  guarantee?: string;
}

type IRegularOrderSummaryItem = Pick<
  IReviewOrderItem,
  'id' | 'designFrontImage' | 'styleCode' | 'price' | 'designRejectionInfo' | 'printTypes'
>;

export interface IRegularOrderShipPriceTotals {
  totalStandardShipPrice: number;
  totalFlashShipPrice: number;
  totalExpeditedShipPrice: number;
  totalEconomyShipPrice: number;
}

/** Per-item ship price totals — same logic as regular order / cart checkout. */
export const computeRegularOrderShipPriceTotals = (
  items: IRegularOrderSummaryItem[],
  sizeFormValues: IRegularOrderSizeForm | undefined
): IRegularOrderShipPriceTotals => {
  const standardPriceTotal = items.reduce((sum, item) => {
    if (!sizeFormValues?.sizes?.[item.id]) {
      return sum;
    }

    const quantTotal = Number(sizeFormValues.sizes[item.id].total) || 0;
    const standardShipPrice = Number(sizeFormValues.sizes[item.id].standardShipPrice) || 0;
    return sum + quantTotal * standardShipPrice;
  }, 0);

  const flashPriceTotal = items.reduce((sum, item) => {
    if (!sizeFormValues?.sizes?.[item.id]) {
      return sum;
    }

    const quantTotal = Number(sizeFormValues.sizes[item.id].total) || 0;
    const flashShipPrice = Number(sizeFormValues.sizes[item.id].flashShipPrice) || 0;
    return sum + quantTotal * flashShipPrice;
  }, 0);

  const expeditedPriceTotal = items.reduce((sum, item) => {
    if (!sizeFormValues?.sizes?.[item.id]) {
      return sum;
    }

    const quantTotal = Number(sizeFormValues.sizes[item.id].total) || 0;
    const expeditedShipPrice = Number(sizeFormValues.sizes[item.id].expeditedShipPrice) || 0;
    return sum + quantTotal * expeditedShipPrice;
  }, 0);

  const economyPriceTotal = items.reduce((sum, item) => {
    if (!sizeFormValues?.sizes?.[item.id]) {
      return sum;
    }

    const quantTotal = Number(sizeFormValues.sizes[item.id].total) || 0;
    const economyShipPrice = Number(sizeFormValues.sizes[item.id].economyShipPrice) || 0;
    return sum + quantTotal * economyShipPrice;
  }, 0);

  return {
    totalStandardShipPrice: standardPriceTotal,
    totalFlashShipPrice: flashPriceTotal,
    totalExpeditedShipPrice: expeditedPriceTotal,
    totalEconomyShipPrice: economyPriceTotal,
  };
};

/** Shipping line amount for the selected ship type — mirrors RegularOrder `setShippingDetails`. */
export const resolveShippingCostForShipTypeKey = (
  shipTypeKey: string,
  totals: IRegularOrderShipPriceTotals
): number => {
  const shipPriceByType: Record<string, number> = {
    flash: totals.totalFlashShipPrice,
    standard: totals.totalStandardShipPrice,
    expedited: totals.totalExpeditedShipPrice,
    economy: totals.totalEconomyShipPrice,
  };

  const shipPrice = shipPriceByType[shipTypeKey];
  return shipPrice === undefined ? 0 : Number(shipPrice.toFixed(2));
};

/** Hook to calculate regular order summary values */
export const useRegularOrderSummary = ({
  items,
  sizeFormValues,
  shippingFormValues,
  fetchSalesTax,
  promoCodeData,
  guarantee,
}: IRegularOrderSummary) => {
  const [subTotal, setSubTotal] = useState<number>(0);
  const [discountedSubTotal, setDiscountedSubTotal] = useState<number>(0);

  const [shipping, setShipping] = useState<number>(0);

  const [salesTax, setSalesTax] = useState<number>(0);
  const [discountSalesTax, setDiscountSalesTax] = useState<number>(0);

  const [total, setTotal] = useState<number>(0);
  const [discountedTotal, setDiscountedTotal] = useState<number>(0);

  const [shippingType, setShippingType] = useState<string>('');

  const [
    calculateSalesTax,
    { isSuccess: isSalesTaxSuccess, isError: isSalesTaxError, isLoading: isSalesTaxLoading },
  ] = useCalculateSalesTaxMutation();

  /** Function to fetch sales tax based on zip & subtotal */
  const getSalesTax = useCallback(async () => {
    // Sales Tax = (Total Price - discount + shipping) x Sales Tax percent
    let discount = '0';

    if (promoCodeData) {
      discount = resolvePromoDiscount(promoCodeData, subTotal, guarantee);
    }

    const guaranteePrice = guarantee === 'FREE' ? 0 : Number(guarantee ?? '0');

    const totalToBeSend = subTotal + (shipping || 0) + guaranteePrice;
    let totalToBeSendDiscounted =
      discountedSubTotal > 0 ? discountedSubTotal + (shipping || 0) + guaranteePrice : 0;

    /** Promo code logic - calculate totalToBeSendDiscounted based on promo code discount and subtotal */
    if (discount) {
      totalToBeSendDiscounted =
        subTotal - +discount > 0 ? subTotal - +discount + (shipping || 0) + guaranteePrice : 0;
    }

    const response: ISalesTaxSuccessRes | IError = await calculateSalesTax({
      zipCode: shippingFormValues.shippingZipCode,
      subTotal: Number(totalToBeSend.toFixed(2)),
      discountedSubTotal: Number(totalToBeSendDiscounted.toFixed(2)),
    });

    if ('data' in response && response.data) {
      setSalesTax((response as ISalesTaxSuccessRes).data.salesTax.salesTaxAmount);
      setDiscountSalesTax((response as ISalesTaxSuccessRes).data.salesTax.discountedSalesTaxAmount);
    }

    if ('error' in response) {
      showToastrMessage('Something went wrong, can you enter zip again?', 'failure');
    }
  }, [
    calculateSalesTax,
    discountedSubTotal,
    shipping,
    shippingFormValues.shippingZipCode,
    subTotal,
    promoCodeData,
    guarantee,
  ]);

  /* Calculate subtotal once when component mounts or when items change */
  const newSubTotal = useMemo(
    () =>
      items.reduce((sum, item) => {
        if (!sizeFormValues?.sizes?.[item.id]) return sum;
        const itemTotal = (sizeFormValues.sizes[item.id].perItemTotal || 0) as number;
        return sum + itemTotal;
      }, 0),
    [items, sizeFormValues]
  );

  /** Calculate sales tax when shipping zip code or subtotal or dicount subtotal changes */
  const handleGetSalesTax = useCallback(() => {
    if (
      fetchSalesTax &&
      validateZipCode(shippingFormValues.shippingZipCode) &&
      (subTotal > 0 || discountedSubTotal > 0)
    ) {
      getSalesTax();
    } else {
      setSalesTax(0);
    }
  }, [
    fetchSalesTax,
    shippingFormValues.shippingZipCode,
    subTotal,
    discountedSubTotal,
    getSalesTax,
  ]);

  /** Calculate total by adding subtotal, shipping and salesTax */
  useEffect(() => {
    let newTotal = (subTotal + shippingFormValues.shippingCost + salesTax).toFixed(2);

    /** Promo code logic - calculate total based on promocode and if discountSalesTax */
    if (promoCodeData) {
      const discount = resolvePromoDiscount(promoCodeData, subTotal, guarantee);
      let salesTaxValue = salesTax;

      if (shouldUseDiscountedSalesTaxForPromo(promoCodeData, +discount)) {
        salesTaxValue = discountSalesTax;
      }

      newTotal = (subTotal - +discount + shippingFormValues.shippingCost + salesTaxValue).toFixed(
        2
      );
    }
    setTotal(parseFloat(newTotal) + (guarantee === 'FREE' ? 0 : Number(guarantee)));
  }, [
    subTotal,
    shippingFormValues.shippingCost,
    salesTax,
    discountSalesTax,
    promoCodeData,
    guarantee,
  ]);

  useEffect(() => {
    handleGetSalesTax();
  }, [handleGetSalesTax]);

  /** Hook to set the shipping cost. */
  useEffect(() => {
    setShipping(shippingFormValues.shippingCost);
    setShippingType(shippingFormValues.shipTypeKey);
  }, [shippingFormValues.shipTypeKey, shippingFormValues.shippingCost]);

  const discountedSubTotalMemo = useMemo(
    () =>
      items.reduce((sum, item) => {
        if (!sizeFormValues?.sizes?.[item.id]) return sum;
        const strikedPerItemTotal = sizeFormValues.sizes[item.id].strikedPerItemTotal
          ? Number(sizeFormValues.sizes[item.id].strikedPerItemTotal)
          : Number(sizeFormValues.sizes[item.id].perItemTotal);

        const itemTotal = Number(strikedPerItemTotal.toFixed(2));
        return sum + itemTotal;
      }, 0),
    [items, sizeFormValues]
  );

  const {
    totalStandardShipPrice,
    totalFlashShipPrice,
    totalExpeditedShipPrice,
    totalEconomyShipPrice,
  } = useMemo(
    () => computeRegularOrderShipPriceTotals(items, sizeFormValues),
    [items, sizeFormValues]
  );

  /** Set subtotal when newSubtotal and discounted total is calculated */
  useEffect(() => {
    setSubTotal(newSubTotal);
    setDiscountedSubTotal(discountedSubTotalMemo);
  }, [discountedSubTotalMemo, newSubTotal]);

  /** Calculate discounted total by adding discount subtotal, shipping and salesTax */
  useEffect(() => {
    if (discountedSubTotal > 0 && discountedSubTotal > total) {
      const newTotal = (
        discountedSubTotal +
        shippingFormValues.shippingCost +
        discountSalesTax
      ).toFixed(2);
      setDiscountedTotal(parseFloat(newTotal));
    } else {
      setDiscountedTotal(0);
    }
  }, [shippingFormValues.shippingCost, discountedSubTotal, discountSalesTax, total]);

  return {
    subTotal,
    shipping,
    salesTax,
    discountSalesTax,
    total,
    discountedTotal,
    isSalesTaxSuccess,
    isSalesTaxError,
    isSalesTaxLoading,
    shippingType,
    totalStandardShipPrice,
    totalExpeditedShipPrice,
    totalFlashShipPrice,
    totalEconomyShipPrice,
  };
};
